if (!customElements.get('product-suggestion-bar')) {
  class ProductSuggestionBar extends HTMLElement {
    connectedCallback() {
      this.storageKey = `psb-dismissed:${this.dataset.sectionId}`;

      if (this.isDismissed()) {
        this.remove();
        return;
      }

      this.activeIndex = 0;
      this.bindSlider();
      this.bindScrollToForm();
      this.bindVariantPickers();
      // The observer owns visibility when it is in play, so only reveal outright
      // when the bar is not tied to the product form.
      if (!this.watchProductForm()) this.reveal();
      this.loadRecommendations();
    }

    disconnectedCallback() {
      this.formObserver?.disconnect();
    }

    /* Product form on the page */

    productFormButton() {
      return (
        document.querySelector('[data-plt-atc]') ||
        document.querySelector('form[action*="/cart/add"] [type="submit"]')
      );
    }

    /* While the real Add to cart / Buy now buttons are on screen the bar would
       only duplicate them, so it stays down and slides up once they scroll away. */

    watchProductForm() {
      if (!this.hasAttribute('data-hide-with-form')) return false;
      if (this.classList.contains('product-suggestion-bar--static')) return false;
      if (!('IntersectionObserver' in window)) return false;

      const button = this.productFormButton();
      if (!button) return false;

      // Watching the whole buy area covers the dynamic checkout button too.
      const target = button.closest('.product-left-thumbs__cart-group') || button.closest('form') || button;

      this.formObserver = new IntersectionObserver(
        (entries) => {
          if (this.isDismissing) return;

          entries.forEach((entry) => {
            this.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );

      this.formObserver.observe(target);
      return true;
    }

    /* When the bar shows the product already on screen, its CTA belongs to the
       product form further up the page rather than to a page load. Delegated from
       the host so it survives the recommendations swap. */

    bindScrollToForm() {
      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-psb-scroll-to-form]')) return;

        const target = this.productFormButton();

        // No form on the page: let the link navigate as written.
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.focus({ preventScroll: true });
      });
    }

    /* Variant picker

       The radios carry the `id` the cart form posts, so the choice itself needs
       no script. Only the price beside them has to follow the selection. Delegated
       from the host so it survives the recommendations swap. */

    bindVariantPickers() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-psb-variant-input]');
        if (!input || !input.checked) return;

        this.updatePrice(input);
      });
    }

    updatePrice(input) {
      const slide = input.closest('[data-psb-slide]') || this;

      const price = slide.querySelector('[data-psb-price]');
      if (price && input.dataset.price) price.textContent = input.dataset.price;

      // Both are in the markup from the start, so a variant on sale only has to
      // fill them in and a variant at full price only has to hide them.
      const compare = slide.querySelector('[data-psb-compare]');
      if (compare) {
        compare.textContent = input.dataset.compareAtPrice || '';
        compare.hidden = !input.dataset.compareAtPrice;
      }

      const discount = slide.querySelector('[data-psb-discount]');
      if (discount) {
        discount.textContent = input.dataset.discount ? `-${input.dataset.discount}%` : '';
        discount.hidden = !input.dataset.discount;
      }
    }

    /* Dismissal */

    storage() {
      const scope = this.dataset.dismissScope;
      if (scope === 'session') return window.sessionStorage;
      if (scope === 'persistent') return window.localStorage;
      return null;
    }

    isDismissed() {
      try {
        return this.storage()?.getItem(this.storageKey) === '1';
      } catch (error) {
        // Private mode and blocked site data both throw here; show the bar.
        return false;
      }
    }

    dismiss() {
      // Set before the transition starts, so a scroll mid transition cannot let
      // the form observer bring the bar back.
      this.isDismissing = true;
      this.formObserver?.disconnect();

      try {
        this.storage()?.setItem(this.storageKey, '1');
      } catch (error) {
        // Not being able to remember the choice must not block closing it.
      }
      this.classList.remove('is-visible');
      this.addEventListener('transitionend', () => this.remove(), { once: true });
      // Static bars have no transition to wait for.
      if (this.classList.contains('product-suggestion-bar--static')) this.remove();
    }

    /* Slider */

    bindSlider() {
      this.track = this.querySelector('[data-psb-track]');
      this.slides = Array.from(this.querySelectorAll('[data-psb-slide]'));
      this.dotsContainer = this.querySelector('[data-psb-dots]');
      this.prevButton = this.querySelector('[data-psb-prev]');
      this.nextButton = this.querySelector('[data-psb-next]');
      this.closeButton = this.querySelector('[data-psb-close]');

      this.prevButton?.addEventListener('click', () => this.step(-1));
      this.nextButton?.addEventListener('click', () => this.step(1));
      this.closeButton?.addEventListener('click', () => this.dismiss());

      this.buildDots();
      this.goTo(0);
    }

    buildDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.textContent = '';
      this.dots = this.slides.map((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product-suggestion-bar__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `${index + 1} / ${this.slides.length}`);
        dot.addEventListener('click', () => this.goTo(index));
        this.dotsContainer.appendChild(dot);
        return dot;
      });
    }

    step(delta) {
      if (!this.slides.length) return;
      const next = (this.activeIndex + delta + this.slides.length) % this.slides.length;
      this.goTo(next);
    }

    goTo(index) {
      if (!this.slides.length || !this.track) return;

      this.activeIndex = index;
      this.track.style.translate = `${index * -100}% 0`;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        // Keep offscreen slides out of the tab order and the a11y tree.
        slide.inert = !isActive;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.dots?.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-selected', dotIndex === index ? 'true' : 'false');
      });
    }

    reveal() {
      // Next frame, so the fixed bar animates up instead of appearing in place.
      requestAnimationFrame(() => this.classList.add('is-visible'));
    }

    /* Shopify product recommendations, when enabled */

    loadRecommendations() {
      const url = this.dataset.recommendationsUrl;
      if (!url) return;

      fetch(`${url}&product_id=${this.dataset.productId}&section_id=${this.dataset.sectionId}`)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((text) => {
          const parsed = document.createElement('div');
          parsed.innerHTML = text;
          const fresh = parsed.querySelector('product-suggestion-bar');

          // Keep the server rendered fallback if there is nothing better.
          if (!fresh || !fresh.querySelector('[data-psb-slide]')) return;

          this.innerHTML = fresh.innerHTML;
          this.activeIndex = 0;
          this.bindSlider();
        })
        .catch(() => {
          // The fallback products are already on screen; nothing to do.
        });
    }
  }

  customElements.define('product-suggestion-bar', ProductSuggestionBar);
}
